import React from "react";
import { Button } from "../..";
import { Modal } from "react-bootstrap";
import { DocumentFilterForm } from "./form";
import { DocumentFilterModalProps, DocumentFilterModalForm } from "./types";
import { DocumentTypeFilter } from "../../../types/DocumentType";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { FormProvider, useForm } from "react-hook-form";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { ClientProjectProvider } from "../../../hooks/ClientProjectContext";
import { ClientDocumentDefaultFilters } from "../../../hooks/FilterContext";
import ThemedModal from "../../utilities/ThemedModal";

export const DocumentFiltersModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultFilters,
    defaultValues,
    dialogId,
    dismiss,
    masterAccountId,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<DocumentFilterModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const formMethods = useForm<DocumentFilterModalForm>();
    const { handleSubmit, reset } = formMethods;

    const onClose = () => cancel!();

    const onClearFilters = () => proceed!({ ...defaultFilters, searchTerm: "" });

    const onSubmit = (form: DocumentFilterModalForm) => proceed!(form);

    // whenever the default values change/we basically open the modal here.
    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset({ ...defaultValues });
    }, [ defaultValues ]);

    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Client Document Filters</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body ref={bodyAnimation}>
                        <DocumentFilterForm isClientLevel={!!masterAccountId} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="warning" type="button" onClick={onClearFilters}>Clear Filters</Button>
                        <Button variant="success" type="submit">Apply Filters</Button>
                        <Button variant="light" type="button" onClick={onClose}>Cancel</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );
}

export function openDocumentFiltersModal(filters: DocumentTypeFilter, searchTerm: string | null, masterAccountId: string | null) {
    return createConfirmation<DocumentFilterModalProps, DocumentFilterModalForm>(confirmable((props) => {
        return masterAccountId
            ? (
                <ClientProjectProvider masterAccountId={masterAccountId}>
                    <DocumentFiltersModal {...props} />
                </ClientProjectProvider>
            )
            : <DocumentFiltersModal {...props} />
    }))({
        dialogId: 'document_filter_modal',
        defaultValues: { ...filters, searchTerm },
        defaultFilters: ClientDocumentDefaultFilters,
        masterAccountId
    });
}